import React from 'react';
import Select from 'react-select';
import { YearSelect } from '../YearSelect';
import vehicleManufacturers from '.././vehicleManufacturers';
import { UserGroupIcon, PlusIcon } from '@heroicons/react/24/outline';

const VehicleForm = ({ customers, selectedCustomer, setSelectedCustomer, formState, handleInputChange, handleYearSelection, addVehicle }) => {
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: '0.75rem',
      borderColor: state.isFocused ? '#4F46E5' : '#E5E7EB',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(79, 70, 229, 0.1)' : 'none',
      '&:hover': {
        borderColor: '#4F46E5',
      },
      padding: '0.125rem',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? '#4F46E5'
        : state.isFocused
        ? '#EEF2FF'
        : 'white',
      color: state.isSelected ? 'white' : '#1F2937',
      cursor: 'pointer',
      '&:active': {
        backgroundColor: '#4338CA',
      },
    }),
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 h-fit sticky top-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl">
            <UserGroupIcon className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-black text-gray-900">Vehicle Registration</h2>
            <p className="text-sm text-gray-600">Add new vehicles to customer accounts</p>
          </div>
        </div>
      </div>

      <form onSubmit={addVehicle} className="space-y-6">
        {/* Customer Selection */}
        <div>
          <label htmlFor="selectedCustomer" className="block text-sm font-bold text-gray-900 mb-2">
            Select Customer <span className="text-red-500">*</span>
          </label>
          <Select
            options={customers}
            onChange={setSelectedCustomer}
            value={selectedCustomer}
            className="text-sm"
            placeholder="Search and select customer..."
            isSearchable
            styles={customSelectStyles}
          />
          {selectedCustomer && (
            <p className="mt-2 text-xs text-green-600 font-medium flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Customer selected
            </p>
          )}
        </div>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center" aria-hidden="true">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-3 bg-white text-gray-500 font-medium">
              Vehicle Details (Optional)
            </span>
          </div>
        </div>

        {/* Vehicle Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Vehicle Make */}
          <div>
            <label htmlFor="vehicleManufacturers" className="block text-sm font-bold text-gray-900 mb-2">
              Vehicle Make
            </label>
            <Select
              options={vehicleManufacturers}
              onChange={(selectedOption) => handleInputChange({ target: { name: 'vehicleMake', value: selectedOption.value } })}
              value={vehicleManufacturers.find(manufacturer => manufacturer.value === formState.vehicleMake)}
              className="text-sm"
              placeholder="Select make..."
              isSearchable
              styles={customSelectStyles}
            />
          </div>

          {/* Vehicle Model */}
          <div>
            <label htmlFor="vehicleModel" className="block text-sm font-bold text-gray-900 mb-2">
              Vehicle Model
            </label>
            <input
              name="vehicleModel"
              value={formState.vehicleModel}
              onChange={handleInputChange}
              placeholder="e.g., Corolla, Civic"
              className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-4 py-2.5 transition-all duration-200"
            />
          </div>

          {/* Year */}
          <div>
            <label htmlFor="Year" className="block text-sm font-bold text-gray-900 mb-2">
              Year
            </label>
            <YearSelect onChange={handleYearSelection} value={formState.year} />
          </div>

          {/* VIN */}
          <div>
            <label htmlFor="VIN" className="block text-sm font-bold text-gray-900 mb-2">
              VIN
            </label>
            <input
              name="VIN"
              value={formState.VIN}
              onChange={handleInputChange}
              placeholder="Vehicle Identification Number"
              className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-4 py-2.5 transition-all duration-200"
            />
          </div>

          {/* Registration Number */}
          <div>
            <label htmlFor="registrationNumber" className="block text-sm font-bold text-gray-900 mb-2">
              Registration Number
            </label>
            <input
              name="registrationNumber"
              value={formState.registrationNumber}
              onChange={handleInputChange}
              placeholder="e.g., ABC-123"
              className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-4 py-2.5 transition-all duration-200"
            />
          </div>

          {/* Engine Type */}
          <div>
            <label htmlFor="engineType" className="block text-sm font-bold text-gray-900 mb-2">
              Engine Type
            </label>
            <input
              name="engineType"
              value={formState.engineType}
              onChange={handleInputChange}
              placeholder="e.g., Petrol, Diesel, Hybrid"
              className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-4 py-2.5 transition-all duration-200"
            />
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 text-base font-bold text-white bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl hover:from-purple-600 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5"
        >
          <PlusIcon className="h-5 w-5" />
          Add Vehicle to Customer
        </button>
      </form>
    </div>
  );
};

export default VehicleForm;
